import { IconButton } from '@mui/material';
import ms from 'ms';
import { type ReactNode } from 'react';
import { toast } from 'react-hot-toast';
import { v4 as uuidv4 } from 'uuid';

import { FunctionalLink } from '~/utils/componentUtils';
import { LOG, log } from '~/utils/logging';

import { Icon } from '@vestigas/ui';

type Lines = string | string[] | ReactNode;

class ToastService {
  MESSAGE: Record<string, ReactNode | string>;
  ID: Record<string, string>;
  TYPE: Record<string, string>;
  alreadyShownDataLoadErrors: string[];
  alreadyShownUpdatedDlns: Array<Record<string, unknown>>;
  dataLoadErrorTimers: string[];

  constructor() {
    this.MESSAGE = {
      ACCOUNTING_REFERENCE_ASSIGNMENT_FAILED:
        'Kostenstelle konnte nicht geändert werden.',
      ARTICLE_OPEN_FAILED: 'Artikel kann nicht geöffnet werden.',
      COMPANY_CREATION_FAILED: 'Firma konnte nicht angelegt werden.',
      COMPANY_LOGO_DELETION_FAILED: 'Firmenlogo konnte nicht entfernt werden.',
      COMPANY_LOGO_LOAD_FAILED: 'Firmenlogo konnte nicht geladen werden.',
      COMPANY_LOGO_UPLOAD_FAILED:
        'Firmenlogo konnte nicht aktualisiert werden. Es werden die folgenden Datenformate unterstützt: .jpg und .png',
      COMPANY_UPDATE_FAILED: 'Firma konnte nicht aktualisiert werden.',
      CONTACT_SUPPORT: (
        <>Bitte kontaktiere den {this.getLink('Support', '/support')}.</>
      ),
      COST_CENTER_CREATION_FAILED: 'Kostenstelle konnte nicht angelegt werden.',
      COST_CENTER_CREATION_FAILED_DUPLICATE:
        'Kostenstelle konnte nicht angelegt werden, da sie bereits im VESTIGAS System vorhanden ist.',
      COST_CENTER_UPDATE_FAILED:
        'Kostenstelle konnte nicht aktualisiert werden.',
      CUSTOMER_SUBSCRIPTION_CREATION_FAILED:
        'Subscription konnte nicht angelegt werden.',
      CUSTOMER_SUBSCRIPTION_UPDATE_FAILED:
        'Subscription konnte nicht aktualisiert werden.',
      DLN_CHANGES_LOAD_FAILED:
        'Lieferungsänderungen konnten nicht geladen werden.',
      DLN_DOWNLOAD_NONE_SELECTED: 'Bitte wähle mindestens eine Lieferung aus.',
      DLN_DOWNLOAD_TOO_MANY_SELECTED:
        'Bitte wähle maximal 300 Lieferungen aus.',
      DLN_LOAD_FAILED: 'Lieferung konnte nicht vollständig geladen werden.',
      INV_DOWNLOAD_NONE_SELECTED: 'Bitte wähle mindestens eine Rechnung aus.',
      INV_DOWNLOAD_TOO_MANY_SELECTED: 'Bitte wähle maximal 100 Rechnungen aus.',
      INVOICE_LOAD_FAILED: 'Rechnung konnte nicht geladen werden.',
      INVOICE_RECEIVERS_LOAD_FAILED:
        'Rechnungsadressen konnten nicht geladen werden.',
      PDF_DOWNLOAD_FAILED: 'PDF konnte nicht geladen werden.',
      PDF_DOWNLOAD_FAILED_NOT_AVAILABLE:
        'Dieser Download-Typ ist nicht verfügbar. Bitte wähle einen anderen Download-Typ.',
      PDF_TEMPLATE_CREATION_FAILED: 'PDF Vorlage konnte nicht angelegt werden.',
      PDF_TEMPLATE_DELETION_FAILED:
        'PDF Template konnte nicht entfernt werden.',
      PDF_TEMPLATE_DOWNLOAD_FAILED:
        'PDF Vorlage konnte nicht heruntergeladen werden.',
      SITE_CREATION_FAILED: 'Standort konnte nicht angelegt werden.',
      SITE_IDENTIFIER_DELETION_FAILED:
        'Standortbezeichnung konnte nicht entfernt werden.',
      SITE_IDENTIFIER_DELETION_SUCCESSFUL:
        'Standortbezeichnung wurde erfolgreich entfernt.',
      SITE_UPDATE_FAILED: 'Standort konnte nicht aktualisiert werden.',
      UNAUTHORIZED_ERROR:
        'Daten konnten nicht synchronisiert werden, da du keine gültige Session hast.',
      UNAUTHORIZED_ERROR_REDIRECT: (
        <>
          Bitte logge dich aus und wieder ein. Weitere Informationen findest du
          in den {this.getLink('FAQs', '/support/faq')}.
        </>
      ),
      USER_CREATION_FAILED: 'Benutzer konnte nicht angelegt werden.',
      USER_CREATION_FAILED_DUPLICATE:
        'Benutzer konnte nicht angelegt werden, da er bereits im VESTIGAS System vorhanden ist.',
      USER_DEFAULT_SIGNATURE_ROLE_UPDATE_FAILED:
        'Die Default-Signaturberechtigung für den folgenden Benutzer konnte nicht aktualisiert werden:',
      USER_FEATURE_FLAGS_UPDATE_FAILED:
        'Die Feature-Flags für den folgenden Benutzer konnten nicht aktualisiert werden:',
      USER_PROFILE_PICTURE_DELETION_FAILED:
        'Profilbild konnte nicht entfernt werden.',
      USER_PROFILE_PICTURE_UPDATE_FAILED:
        'Profilbild konnte nicht aktualisiert werden. Es werden die folgenden Datenformate unterstützt: .jpg und .png',
      USER_PUSH_NOTIFICATIONS_UPDATE_FAILED:
        'Die Push Benachrichtigungen für den folgenden Benutzer konnte nicht aktualisiert werden:',
      USER_SIGNATURE_ROLE_UPDATE_FAILED:
        'Die Signaturberechtigung für den folgenden Benutzer konnte nicht aktualisiert werden:',
      USER_TYPE_UPDATE_FAILED: 'Benutzertyp konnte nicht aktualisiert werden.',
      USER_UPDATE_FAILED: 'Benutzerdaten konnten nicht aktualisiert werden.',
      VEHICLE_CREATION_FAILED: 'Fahrzeug konnte nicht angelegt werden.',
      VEHICLE_CREATION_FAILED_DUPLICATE:
        'Fahrzeug konnte nicht angelegt werden, da das Kennzeichen bereits im VESTIGAS System vorhanden ist.',
      VEHICLE_INVALID_INPUT:
        'Fahrzeug kann nicht angelegt werden, da die Eingabe des Kennzeichens ungültig ist.',
      VEHICLE_UPDATE_FAILED: 'Fahrzeug konnte nicht aktualisiert werden.',
      VEHICLE_UPDATE_FAILED_DUPLICATE:
        'Fahrzeug konnte nicht aktualisiert werden, da das Kennzeichen bereits im VESTIGAS System vorhanden ist.',
    };

    this.ID = {
      CREATE_DLN_INVALID_INPUT: 'create-dln-invalid-input',
      DLN_DOWNLOAD_NONE_SELECTED: 'dln-download-none-selected',
      DLN_DOWNLOAD_TOO_MANY_SELECTED: 'dln-download-too-many-selected',
      DLN_DOWNLOAD_WAIT: 'dln-download-wait',
      DLN_PAGE_CHANGE: 'dln-page-change',
      INV_DOWNLOAD_NONE_SELECTED: 'inv-download-none-selected',
      INV_DOWNLOAD_TOO_MANY_SELECTED: 'inv-download-too-many-selected',
      INV_DOWNLOAD_WAIT: 'inv-download-wait',
      RATE_LIMIT_ERROR: 'rate-limit-error',
      SESSION_EXPIRED_LOGOUT: 'session-expired-logout',
      UNAUTHORIZED_ERROR: 'unauthorized-error',
      UNAUTHORIZED_WARNING: 'unauthorized-warning',
    };

    this.TYPE = {
      WARNING: 'warning',
    };

    this.alreadyShownDataLoadErrors = [];
    this.alreadyShownUpdatedDlns = [];

    this.dataLoadErrorTimers = [];
  }

  toastContent(lines: Lines, id: string) {
    const onClick = () => {
      log.productAnalyticsEvent('Dismiss toast', LOG.FEATURE.OTHER_FEATURE);
      toast.dismiss(id);
    };

    return (
      <span className="flex flex-row items-center">
        <div>
          {Array.isArray(lines) ? (
            lines.map((line) => {
              return <div key={line}>{line}</div>;
            })
          ) : (
            <div>{lines}</div>
          )}
        </div>
        <IconButton
          size="large"
          style={{ marginLeft: '10px' }}
          onClick={onClick}
        >
          <Icon metaphor="close" className="size-5" />
        </IconButton>
      </span>
    );
  }

  getBackdropLink(
    message: string,
    link: string,
    backdropDuration: number = ms('3s'),
    backdropMessage: string,
  ) {
    return (
      <FunctionalLink
        backdropDuration={backdropDuration}
        backdropMessage={backdropMessage}
        className="font-bold"
        message={message}
        to={link}
      />
    );
  }

  getLink(message: string, link: string) {
    return (
      <FunctionalLink
        className="main-text font-bold"
        message={message}
        to={link}
      />
    );
  }

  info(lines: Lines, messageId?: string, duration: number = ms('10s')) {
    return toast((t) => this.toastContent([lines].flat(), t.id), {
      className: 'info-toast',
      duration,
      icon: <Icon metaphor="info" className="text-primary300 mr-1" />,
      // Callers pass an id for messages that can fire repeatedly; react-hot-toast
      // then updates the open toast instead of stacking another copy.
      id: messageId,
      style: {
        backgroundColor: '#E4E6ED',
        paddingLeft: '15px',
      },
    });
  }

  warning(lines: Lines, messageId?: string, duration: number = ms('10s')) {
    return toast((t) => this.toastContent([lines].flat(), t.id), {
      duration,
      icon: (
        <Icon metaphor="warningRounded" className="text-warningBase mr-1" />
      ),
      id: messageId,
      style: {
        backgroundColor: '#E4E6ED',
        paddingLeft: '15px',
      },
    });
  }

  error(lines: Lines, messageId?: string, duration: number = ms('15s')) {
    return toast.error((t) => this.toastContent([lines].flat(), t.id), {
      duration,
      id: messageId,
      style: {
        backgroundColor: '#E4E6ED',
        paddingLeft: '20px',
      },
    });
  }

  success(lines: Lines, messageId?: string, duration: number = ms('10s')) {
    return toast.success((t) => this.toastContent([lines].flat(), t.id), {
      duration,
      id: messageId,
      style: {
        backgroundColor: '#E4E6ED',
        paddingLeft: '20px',
      },
    });
  }

  async promise(
    promise: Promise<unknown>,
    loadingLines: string[],
    successLines: string[],
    errorLines: string[],
    id: string,
    duration: number = ms('10s'),
  ) {
    id ??= uuidv4();

    return toast.promise(
      promise,
      {
        error: () => this.toastContent(errorLines, id),
        loading: () => this.toastContent(loadingLines, id),
        success: () => this.toastContent(successLines, id),
      },
      {
        duration: Infinity,
        error: {
          duration,
        },
        id,
        style: {
          backgroundColor: '#E4E6ED',
          paddingLeft: '20px',
        },
        success: {
          duration,
        },
      },
    );
  }

  dismiss(id: string) {
    toast.dismiss(id);
  }

  httpError(
    lines: string[],
    errorResponse: any,
    type?: string,
    withBackendDescription?: boolean,
  ) {
    switch (errorResponse?.status) {
      case 401: {
        if (type === this.TYPE.WARNING) {
          this.warning(
            [
              this.MESSAGE.UNAUTHORIZED_ERROR,
              this.MESSAGE.UNAUTHORIZED_ERROR_REDIRECT,
            ],
            this.ID.UNAUTHORIZED_WARNING,
          );
        } else {
          this.error(
            [
              this.MESSAGE.UNAUTHORIZED_ERROR,
              this.MESSAGE.UNAUTHORIZED_ERROR_REDIRECT,
            ],
            this.ID.UNAUTHORIZED_ERROR,
          );
        }

        break;
      }

      case 403: {
        if (type === this.TYPE.WARNING) {
          this.warning(['Fehlende Berechtigung:', ...lines]);
        } else {
          this.error(['Fehlende Berechtigung:', ...lines]);
        }

        break;
      }

      case 400:
      case 404:
      case 405:
      case 500:
      case 502:
      case 503: {
        // prevent that user is spammed with data load errors
        if (this.isLoadMessage(lines[0])) {
          if (!this.alreadyShownDataLoadErrors.includes(lines[0] ?? '')) {
            if (type === this.TYPE.WARNING) {
              this.warning([...lines, this.MESSAGE.CONTACT_SUPPORT]);
            } else {
              this.error([...lines, this.MESSAGE.CONTACT_SUPPORT]);
            }

            this.resetDataLoadErrorTimer(lines[0] ?? '');
          }
        } else if (type === this.TYPE.WARNING) {
          this.warning([...lines, this.MESSAGE.CONTACT_SUPPORT]);
        } else {
          this.error([...lines, this.MESSAGE.CONTACT_SUPPORT]);
        }

        break;
      }

      case 422: {
        if (Array.isArray(errorResponse?.data?.detail)) {
          if (errorResponse?.data?.detail)
            for (const validationError of errorResponse?.data?.detail ?? []) {
              lines.push(
                this.getValidationError(
                  validationError,
                  withBackendDescription,
                ),
              );
            }
        } else if (typeof errorResponse?.data?.detail.len === 'string') {
          lines.push(
            this.getValidationError(
              errorResponse.data.detail,
              withBackendDescription,
            ),
          );
        }

        if (type === this.TYPE.WARNING) {
          this.warning(lines);
        } else {
          this.error(lines);
        }

        break;
      }

      default: {
        if (type === this.TYPE.WARNING) {
          this.warning(lines);
        } else {
          this.error(lines);
        }

        break;
      }
    }
  }

  getValidationError(validationError: any, withBackendDescription?: boolean) {
    switch (validationError.type) {
      case 'value_error.email': {
        return 'E-Mail Adresse ungültig.';
      }

      case 'value_error.custom_error': {
        return validationError.msg;
      }

      case 'value_error': {
        if (validationError.loc?.includes('latitude')) {
          return 'Breitengrad ungültig.';
        }

        if (validationError.loc?.includes('longitude')) {
          return 'Längengrad ungültig.';
        }

        break;
      }

      default: {
        if (withBackendDescription) {
          return [
            JSON.stringify(validationError.loc),
            validationError.msg,
          ].join('\u00A0\u00A0→\u00A0\u00A0');
        }

        return 'Ungültige Eingabe eines Feldes.';
      }
    }
  }

  resetDataLoadErrorTimer(message: string) {
    this.alreadyShownDataLoadErrors.push(message);

    this.dataLoadErrorTimers[message] = setTimeout(() => {
      this.alreadyShownDataLoadErrors = this.alreadyShownDataLoadErrors.filter(
        (item) => item !== message,
      );
    }, ms('2s'));
  }

  dlnInfo(lines: Lines, dlns: any[], hasBackdrop?: boolean) {
    const now = Date.now();

    // to prevent duplicates, don't show dln in toast if it has been shown in the last 10 seconds
    dlns = dlns.filter((dln) => {
      let dlnIsNew = true;
      for (const alreadyShownDln of this.alreadyShownUpdatedDlns) {
        if (
          alreadyShownDln.id === dln.id &&
          now - alreadyShownDln.date <= 10_000
        ) {
          dlnIsNew = false;
        }
      }

      return dlnIsNew;
    });

    // remove all dlns from this.alreadyShownUpdatedDlns that are older that 10 seconds to prevent that it gets too big
    this.alreadyShownUpdatedDlns = this.alreadyShownUpdatedDlns.filter(
      (item) => {
        return !(now - item.date >= 10_000);
      },
    );

    this.alreadyShownUpdatedDlns = [
      ...this.alreadyShownUpdatedDlns,
      ...dlns.map((item) => {
        return {
          date: now,
          id: item.id,
        };
      }),
    ];

    if (dlns.length === 0) {
      return;
    }

    const dlnList = [];

    for (const [index, dln] of dlns.entries()) {
      if (index === 6) {
        dlnList.push(<div key={index}>…</div>);
        break;
      }

      if (hasBackdrop) {
        dlnList.push(
          <div key={dln.dlnNr}>
            {this.getBackdropLink(
              dln.dlnNr,
              `/deliveries/${dln?.id}?source=updateHandler`,
              ms('5s'),
              'Lieferdaten werden synchronisiert',
            )}
          </div>,
        );
      } else {
        dlnList.push(
          <div key={dln.dlnNr}>
            {this.getLink(
              dln.dlnNr,
              `/deliveries/${dln?.id}?source=updateHandler`,
            )}
          </div>,
        );
      }
    }

    this.info([...lines, dlnList]);
  }

  isLoadMessage(inputMessage?: string) {
    const constMessage = Object.keys(this.MESSAGE).find(
      (x) => this.MESSAGE[x] === inputMessage,
    );

    return constMessage?.includes('LOAD');
  }
}

const toastServiceInstance = new ToastService();

export default toastServiceInstance;
